import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, User, DollarSign, Send, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GlassCard, API } from './GlassCard';

export default function Inquiries() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteModal, setQuoteModal] = useState(null);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [quoteDesc, setQuoteDesc] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [quoteDays, setQuoteDays] = useState('');
  const [quoteSending, setQuoteSending] = useState(false);

  useEffect(() => {
    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInquiries = async () => {
    try {
      const res = await fetch(`${API}/messages/inquiries`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setInquiries(data.inquiries || []);
      }
    } catch (e) {
      console.error('Failed to fetch inquiries:', e);
    } finally {
      setLoading(false);
    }
  };

  const sendQuote = async () => {
    if (!quoteTitle.trim() || !quotePrice || !quoteDays) {
      toast.error('Fill in title, price, and delivery days');
      return;
    }
    setQuoteSending(true);
    try {
      const res = await fetch(`${API}/messages/send-offer`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: quoteModal.sender_id,
          offer_title: quoteTitle,
          offer_description: quoteDesc,
          offer_price: parseFloat(quotePrice),
          delivery_days: parseInt(quoteDays),
        }),
      });
      if (res.ok) {
        toast.success(`Quote sent to ${quoteModal.sender_name}!`);
        setQuoteModal(null);
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to send quote');
      }
    } catch {
      toast.error('Failed to send');
    } finally {
      setQuoteSending(false);
    }
  };

  const openQuote = (e, inq) => {
    e.stopPropagation();
    setQuoteModal(inq);
    setQuoteTitle('');
    setQuoteDesc(inq.service_description || '');
    setQuotePrice('');
    setQuoteDays('');
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="vendor-inquiries">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white font-syne flex items-center gap-2"><FileText size={22} className="text-[#A78BFA]" /> Service Inquiries</h2>
          <p className="text-[#9CA3AF] text-sm mt-1">{inquiries.length} inquiry request{inquiries.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {inquiries.length === 0 ? (
        <GlassCard className="text-center py-16 px-6">
          <FileText size={48} className="mx-auto text-[#64748B] mb-4" />
          <h3 className="text-white font-syne text-xl font-bold mb-2">No inquiries yet</h3>
          <p className="text-[#9CA3AF] text-sm max-w-md mx-auto">When customers click "Book Now" on your profile, their service requests will appear here.</p>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inq) => (
            <GlassCard key={inq.message_id} className="p-5 hover:border-[#8B5CF6]/20 transition-all cursor-pointer" data-testid={`inquiry-${inq.message_id}`} onClick={() => navigate(`/dashboard/vendor/inquiries/${inq.message_id}`)}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {inq.sender_picture ? <img src={inq.sender_picture} className="w-full h-full object-cover" alt="" /> : <User size={18} className="text-[#A78BFA]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-white font-semibold font-outfit truncate">{inq.sender_name}</p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!inq.is_read && <span className="w-2.5 h-2.5 rounded-full bg-[#8B5CF6] animate-pulse" />}
                      <span className="text-[#64748B] text-[10px]">{inq.created_at ? new Date(inq.created_at).toLocaleDateString() : ''}</span>
                    </div>
                  </div>
                  <p className="text-[#C4B5FD] text-sm font-outfit line-clamp-2 mb-2">{inq.service_description}</p>
                  {inq.attachment_urls?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`inquiry-attachments-${inq.message_id}`}>
                      {inq.attachment_urls.slice(0, 6).map((url, i) => (
                        <a
                          key={i}
                          href={url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="block w-12 h-12 rounded-lg overflow-hidden border border-white/10 hover:border-[#8B5CF6]/50 transition-colors"
                          title={`Reference photo ${i + 1}`}
                        >
                          <img src={url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {inq.is_custom_project && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                        ✨ Custom project
                      </span>
                    )}
                    {inq.budget && (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${inq.budget.includes('Requesting') ? 'bg-[#FCD34D]/10 text-[#FCD34D] border border-[#FCD34D]/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'}`}>
                        <DollarSign size={11} /> {inq.budget}
                      </span>
                    )}
                    <button onClick={(e) => openQuote(e, inq)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-[#8B5CF6]/15 text-[#A78BFA] border border-[#8B5CF6]/20 hover:bg-[#8B5CF6]/25 transition-all" data-testid={`send-quote-${inq.message_id}`}>
                      <DollarSign size={11} /> Send Quote
                    </button>
                  </div>
                </div>
                <button className="p-2 rounded-lg bg-[#8B5CF6]/10 text-[#A78BFA] hover:bg-[#8B5CF6]/20 transition-all flex-shrink-0" onClick={(e) => { e.stopPropagation(); navigate(`/messages/${inq.sender_id}`); }}>
                  <Send size={16} />
                </button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {quoteModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setQuoteModal(null)}>
          <div className="bg-[#1A1A3E] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="quote-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-syne font-bold text-lg">Send Quote</h3>
              <button onClick={() => setQuoteModal(null)} className="text-[#64748B] hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-[#9CA3AF] text-sm mb-4">To: <strong className="text-white">{quoteModal.sender_name}</strong></p>
            <div className="space-y-3">
              <div>
                <label className="text-[#9CA3AF] text-xs mb-1 block">Service Title *</label>
                <input value={quoteTitle} onChange={(e) => setQuoteTitle(e.target.value)} placeholder="e.g., Custom Logo Design" className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3" data-testid="quote-title" />
              </div>
              <div>
                <label className="text-[#9CA3AF] text-xs mb-1 block">Description</label>
                <textarea value={quoteDesc} onChange={(e) => setQuoteDesc(e.target.value)} rows={3} placeholder="Describe what's included..." className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3" data-testid="quote-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#9CA3AF] text-xs mb-1 block">Price ($) *</label>
                  <input type="number" value={quotePrice} onChange={(e) => setQuotePrice(e.target.value)} placeholder="250" className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3" data-testid="quote-price" />
                </div>
                <div>
                  <label className="text-[#9CA3AF] text-xs mb-1 block">Delivery (days) *</label>
                  <input type="number" value={quoteDays} onChange={(e) => setQuoteDays(e.target.value)} placeholder="7" className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3" data-testid="quote-days" />
                </div>
              </div>
              <button onClick={sendQuote} disabled={quoteSending} className="w-full py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold text-sm mt-2" data-testid="send-quote-submit">
                {quoteSending ? 'Sending...' : 'Send Quote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
